import { useEffect, useState } from "react";

export interface DeviceInfo {
  platform: string;
  platformVersion: string;
  browserName: string;
  browserVersion: string;
  isMobile: boolean;
  architecture: string;
}

const defaultDeviceInfo: DeviceInfo = {
  platform: "",
  platformVersion: "",
  browserName: "",
  browserVersion: "",
  isMobile: false,
  architecture: "",
};

/**
 * Collects enhanced device information using navigator.userAgentData (if available)
 * with fallback to navigator.userAgent parsing.
 */
export function useDeviceInfo() {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(defaultDeviceInfo);

  useEffect(() => {
    async function collectDeviceInfo() {
      const nav = navigator as any;

      // Modern API: navigator.userAgentData
      if (nav.userAgentData) {
        try {
          const uaData = await nav.userAgentData.getHighEntropyValues(["platform", "platformVersion", "architecture", "model", "brands"]);

          const brand = uaData.brands?.find((b: any) => !b.brand.includes("Not") && !b.brand.includes("Chromium")) || uaData.brands?.[0];

          setDeviceInfo({
            platform: uaData.platform || "",
            platformVersion: uaData.platformVersion || "",
            browserName: brand?.brand || "",
            browserVersion: brand?.version || "",
            isMobile: nav.userAgentData.mobile || false,
            architecture: uaData.architecture || "",
          });

          return;
        } catch {
          // Fall through to userAgent parsing
        }
      }

      // Fallback: parse userAgent string
      const ua = navigator.userAgent;
      let platform = "Unknown";
      let platformVersion = "";
      let browserName = "Unknown";
      let browserVersion = "";
      const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      // Detect platform
      if (/Windows NT (\d+\.\d+)/.test(ua)) {
        platform = "Windows";
        platformVersion = RegExp.$1;
      } else if (/Mac OS X (\d+[._]\d+[._]?\d*)/.test(ua)) {
        platform = "macOS";
        platformVersion = RegExp.$1.replace(/_/g, ".");
      } else if (/Android (\d+\.?\d*)/.test(ua)) {
        platform = "Android";
        platformVersion = RegExp.$1;
      } else if (/iPhone OS (\d+_\d+)/.test(ua)) {
        platform = "iOS";
        platformVersion = RegExp.$1.replace(/_/g, ".");
      } else if (/Linux/.test(ua)) {
        platform = "Linux";
      }

      // Detect browser
      if (/Edg\/(\d+\.?\d*)/.test(ua)) {
        browserName = "Edge";
        browserVersion = RegExp.$1;
      } else if (/Chrome\/(\d+\.?\d*)/.test(ua) && !/Edg/.test(ua)) {
        browserName = "Chrome";
        browserVersion = RegExp.$1;
      } else if (/Firefox\/(\d+\.?\d*)/.test(ua)) {
        browserName = "Firefox";
        browserVersion = RegExp.$1;
      } else if (/Safari\/(\d+\.?\d*)/.test(ua) && !/Chrome/.test(ua)) {
        browserName = "Safari";
        // Safari version from Version/ field
        if (/Version\/(\d+\.?\d*)/.test(ua)) browserVersion = RegExp.$1;
      }

      setDeviceInfo({ platform, platformVersion, browserName, browserVersion, isMobile, architecture: "" });
    }

    collectDeviceInfo();
  }, []);

  return deviceInfo;
}
